import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { useFelaEnhanced } from '@ackee/fela';

import { DynamicContainer } from '../Layout/DynamicContainer';
import { ContainerType } from '../Layout/constants';
import Text, { TextTypes } from '../Text';

import * as felaRules from './Technologies.styles';

interface Item {
    id: string;
    title: MessageDescriptor['id'];
    image: string;
    link?: MessageDescriptor['id'];
}

export interface TechnologyCategory {
    name: MessageDescriptor['id'];
    sectionTitle?: MessageDescriptor['id'];
    items: Item[];
}

interface TechnologiesProps {
    content: TechnologyCategory;
    isCareer?: boolean;
    fade?: boolean;
    isDevelopmentPage?: boolean;
}

export const Technologies = ({
    content,
    isCareer = false,
    fade = false,
    isDevelopmentPage = false,
}: TechnologiesProps) => {
    const intl = useIntl();
    const { styles, rules } = useFelaEnhanced(felaRules, { fade, isDevelopmentPage });
    const { formatMessage } = intl;

    return (
        <>
            {!isCareer && (
                // @ts-expect-error
                <Text as="h3" extend={{ text: rules.extendText }} columns={12} type={TextTypes.SECTION}>
                    <FormattedMessage id={content.sectionTitle} />
                </Text>
            )}
            <DynamicContainer<{
                fade: TechnologiesProps['fade'];
                isDevelopmentPage: TechnologiesProps['isDevelopmentPage'];
            }>
                isDevelopmentPage={isDevelopmentPage}
                fade={fade}
                extend={{ container: felaRules.extendContainer }}
                type={ContainerType.GRID}
            >
                {content.items.map(item => {
                    const formattedTitle = formatMessage({ id: item.title });
                    const formattedLink = item.link && formatMessage({ id: item.link });
                    return (
                        <DynamicContainer<{ key: Item['id'] }>
                            extend={{ container: felaRules.extendItemContainer }}
                            key={item.id}
                            columns={1}
                        >
                            <div className={styles.imageContainer}>
                                <img src={item.image} className={styles.image} alt={formattedTitle} />
                            </div>
                            {item.link ? (
                                <a
                                    href={formattedLink}
                                    className={styles.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {formattedTitle}
                                </a>
                            ) : (
                                <span>{formattedTitle}</span>
                            )}
                        </DynamicContainer>
                    );
                })}
            </DynamicContainer>
        </>
    );
};
